import parse from "parse-duration";

export type ClientCookieOptions = {
  /** Session cookie when omitted. Cookie `max-age` in seconds. */
  maxAgeSeconds?: number;
  /**
   * Duration string (e.g. `"90d"`, `"1h"`) parsed via `parse-duration`.
   * Ignored when `maxAgeSeconds` is set.
   */
  maxAge?: string;
  path?: string;
  sameSite?: "lax" | "strict" | "none";
  secure?: boolean;
};

function resolveExpires(options: ClientCookieOptions): {
  expires?: string;
  maxAge?: number;
} {
  if (options.maxAgeSeconds != null) {
    return { maxAge: options.maxAgeSeconds };
  }

  if (options.maxAge) {
    const maxAgeSeconds = parse(options.maxAge, "s");
    if (maxAgeSeconds == null || maxAgeSeconds <= 0) return {};
    return {
      expires: new Date(Date.now() + maxAgeSeconds * 1000).toUTCString(),
      maxAge: maxAgeSeconds,
    };
  }

  return {};
}

/** Read a non-httpOnly cookie from `document.cookie`. */
export function getClientCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;

  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (!part.startsWith(prefix)) continue;
    try {
      return decodeURIComponent(part.slice(prefix.length));
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Write a non-httpOnly cookie via `document.cookie`. */
export function setClientCookie(
  name: string,
  value: string,
  options: ClientCookieOptions = {},
): void {
  if (typeof document === "undefined") return;

  const { path = "/", sameSite, secure } = options;
  const { expires, maxAge } = resolveExpires(options);

  let cookie = `${name}=${encodeURIComponent(value)}; path=${path}`;
  if (expires) cookie += `; expires=${expires}`;
  if (maxAge != null) cookie += `; max-age=${maxAge}`;
  if (sameSite) cookie += `; SameSite=${sameSite}`;
  if (secure) cookie += "; Secure";

  // deepcode ignore WebCookieSecureDisabledByDefault: Secure is opt-in; callers set it for HTTPS cookies
  document.cookie = cookie;
}

/** Delete a non-httpOnly cookie by expiring it. */
export function deleteClientCookie(
  name: string,
  options: Pick<ClientCookieOptions, "path"> = {},
): void {
  setClientCookie(name, "", {
    path: options.path ?? "/",
    maxAgeSeconds: 0,
  });
}
